import { apiSaga } from './sagas'
import { call, take, select, put, all } from 'redux-saga/effects'
import omit from 'lodash/omit'
import { push } from 'connected-react-router'
import {
  addItemsToCart,
  removeItemFromCart,
  loadOrderCart,
  loadCheckoutCart,
  callApi,
  loadMenuItem
} from '../../api/api'
import { checkoutRoute, checkoutInterstitialRoute } from '../../routes/routes'
import {
  selectNormalizedMenuItem,
  selectIsAuthenticated,
  selectCurrentDeliveryDropoff
} from './selectors'
import { formatUrl } from '../../util/formatUtils'
import { normalizeErrorResponse, logException } from '../../util/errorUtils'
import { loadMenuItemSuccess, loadMenuItemFailure } from './personalize'
import { pascalcaseKeys } from '../../util/keys'
import {
  getLocalCart,
  addItemToLocalCart,
  removeItemFromLocalCart,
  updateItemFromLocalCart
} from '../../util/webStorageUtils'

// ------------------------------------
// CONSTs
// ------------------------------------
const CHECKOUT_ERROR = 'There was an error checking out.'
const REMOVE_ITEM_ERROR = 'There was an error removing the item from your cart.'
const LOAD_ERROR = 'There was an error loading your cart.'
const ADD_ITEM_ERROR = 'There was an error adding the item to your cart.'

// ------------------------------------
// Action Types & Creators
// ------------------------------------

export const LOAD_ORDER_CART = 'foodsby/cart/LOAD_ORDER_CART'
export const LOAD_ORDER_CART_SUCCESS = 'foodsby/cart/LOAD_ORDER_CART_SUCCESS'
export const LOAD_ORDER_CART_FAILURE = 'foodsby/cart/LOAD_ORDER_CART_FAILURE'
export const LOAD_CHECKOUT_CART = 'foodsby/cart/LOAD_CHECKOUT_CART'
export const LOAD_CHECKOUT_CART_SUCCESS =
  'foodsby/cart/LOAD_CHECKOUT_CART_SUCCESS'
export const LOAD_CHECKOUT_CART_FAILURE =
  'foodsby/cart/LOAD_CHECKOUT_CART_FAILURE'

export const ADD_ITEM_TO_CART = 'foodsby/cart/ADD_ITEM_TO_CART'
export const ADD_ITEM_TO_CART_SUCCESS = 'foodsby/cart/ADD_ITEM_TO_CART_SUCCESS'
export const ADD_ITEM_TO_CART_FAILURE = 'foodsby/cart/ADD_ITEM_TO_CART_FAILURE'

export const ADD_HASH_TO_CART = 'foodsby/cart/ADD_HASH_TO_CART'
export const ADD_HASH_TO_CART_SUCCESS = 'foodsby/cart/ADD_HASH_TO_CART_SUCCESS'
export const ADD_HASH_TO_CART_FAILURE = 'foodsby/cart/ADD_HASH_TO_CART_FAILURE'

export const REMOVE_ITEM_FROM_ORDER_CART =
  'foodsby/cart/REMOVE_ITEM_FROM_ORDER_CART'
export const REMOVE_ITEM_FROM_ORDER_CART_SUCCESS =
  'foodsby/cart/REMOVE_ITEM_FROM_ORDER_CART_SUCCESS'
export const REMOVE_ITEM_FROM_ORDER_CART_FAILURE =
  'foodsby/cart/REMOVE_ITEM_FROM_ORDER_CART_FAILURE'

export const REMOVE_ITEM_FROM_CHECKOUT_CART =
  'foodsby/cart/REMOVE_ITEM_FROM_CHECKOUT_CART'
export const REMOVE_ITEM_FROM_CHECKOUT_CART_SUCCESS =
  'foodsby/cart/REMOVE_ITEM_FROM_CHECKOUT_CART_SUCCESS'
export const REMOVE_ITEM_FROM_CHECKOUT_CART_FAILURE =
  'foodsby/cart/REMOVE_ITEM_FROM_CHECKOUT_CART_FAILURE'

export const LOAD_EDIT_ORDER = 'foodsby/cart/LOAD_EDIT_ORDER'
export const LOAD_EDIT_ORDER_SUCCESS = 'foodsby/cart/LOAD_EDIT_ORDER_SUCCESS'
export const LOAD_EDIT_ORDER_FAILURE = 'foodsby/cart/LOAD_EDIT_ORDER_FAILURE'

export const EDIT_ORDER_IN_CART = 'foodsby/cart/EDIT_ORDER_IN_CART'
export const EDIT_ORDER_IN_CART_SUCCESS =
  'foodsby/cart/EDIT_ORDER_IN_CART_SUCCESS'
export const EDIT_ORDER_IN_CART_FAILURE =
  'foodsby/cart/EDIT_ORDER_IN_CART_FAILURE'

export const CANCEL_EDITING_ORDER = 'foodsby/cart/CANCEL_EDITING_ORDER'

export const loadOrderCartStart = deliveryDropoffId => {
  return {
    type: LOAD_ORDER_CART,
    payload: { deliveryDropoffId }
  }
}

export const loadOrderCartSuccess = orderCart => {
  if (!orderCart) {
    return {
      type: LOAD_ORDER_CART_SUCCESS,
      payload: {
        orderCart: {
          additionalInstructions: null,
          orderItems: []
        }
      }
    }
  } else {
    return {
      type: LOAD_ORDER_CART_SUCCESS,
      payload: { orderCart }
    }
  }
}

export const loadOrderCartFailure = error => {
  return {
    type: LOAD_ORDER_CART_FAILURE,
    error
  }
}

export const loadCheckoutCartStart = deliveryDropoffId => {
  return {
    type: LOAD_CHECKOUT_CART,
    payload: { deliveryDropoffId }
  }
}

export const loadCheckoutCartSuccess = checkoutCart => {
  return {
    type: LOAD_CHECKOUT_CART_SUCCESS,
    payload: { checkoutCart }
  }
}

export const loadCheckoutCartFailure = error => {
  return {
    type: LOAD_CHECKOUT_CART_FAILURE,
    error
  }
}

export const addItemToCartStart = (
  locationId,
  menuItemId,
  dropoffId,
  formData,
  checkout = false
) => {
  return {
    type: ADD_ITEM_TO_CART,
    payload: { locationId, menuItemId, dropoffId, formData, checkout }
  }
}

export const addItemToCartSuccess = response => {
  return {
    type: ADD_ITEM_TO_CART_SUCCESS,
    payload: response
  }
}

export const addItemToCartFailure = error => {
  return {
    type: ADD_ITEM_TO_CART_FAILURE,
    error
  }
}

export const removeItemFromOrderCartStart = (menuItemId, dropoffId) => {
  return {
    type: REMOVE_ITEM_FROM_ORDER_CART,
    payload: { menuItemId, dropoffId }
  }
}

export const removeItemFromOrderCartSuccess = response => {
  return {
    type: REMOVE_ITEM_FROM_ORDER_CART_SUCCESS,
    payload: response
  }
}

export const removeItemFromOrderCartFailure = error => {
  return {
    type: REMOVE_ITEM_FROM_ORDER_CART_FAILURE,
    error
  }
}

export const removeItemFromCheckoutCartStart = (menuItemId, dropoffId) => {
  return {
    type: REMOVE_ITEM_FROM_CHECKOUT_CART,
    payload: { menuItemId, dropoffId }
  }
}

export const removeItemFromCheckoutCartSuccess = response => {
  return {
    type: REMOVE_ITEM_FROM_CHECKOUT_CART_SUCCESS,
    payload: response
  }
}

export const removeItemFromCheckoutCartFailure = error => {
  return {
    type: REMOVE_ITEM_FROM_CHECKOUT_CART_FAILURE,
    error
  }
}

export const loadEditOrder = (
  hash,
  orderItemId,
  isEditingPastOrder,
  userId,
  menuItemId
) => {
  return {
    type: LOAD_EDIT_ORDER,
    payload: {
      hash,
      orderItemId,
      isEditingPastOrder,
      userId,
      menuItemId
    }
  }
}

export const loadEditOrderSuccess = response => {
  return {
    type: LOAD_EDIT_ORDER_SUCCESS,
    payload: response
  }
}

export const loadEditOrderFailure = error => {
  return {
    type: LOAD_EDIT_ORDER_FAILURE,
    error
  }
}

export const editOrderInCart = (
  locationId,
  menuItemId,
  dropoffId,
  formData,
  orderItemId,
  isEditingFromMenu,
  checkout = false
) => {
  return {
    type: EDIT_ORDER_IN_CART,
    payload: {
      locationId,
      menuItemId,
      dropoffId,
      formData,
      orderItemId,
      isEditingFromMenu,
      checkout
    }
  }
}

export const editOrderInCartSuccess = () => {
  return {
    type: EDIT_ORDER_IN_CART_SUCCESS
  }
}

export const editOrderInCartFailure = error => {
  return {
    type: EDIT_ORDER_IN_CART_FAILURE,
    payload: error
  }
}

export const cancelEditingOrder = () => {
  return {
    type: CANCEL_EDITING_ORDER
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [LOAD_ORDER_CART]: state => {
    return {
      ...state,
      isOrderCartLoading: true,
      orderCartError: undefined,
      editOrderError: undefined
    }
  },
  [LOAD_ORDER_CART_SUCCESS]: (state, action) => {
    const { orderCart } = action.payload
    return {
      ...state,
      isOrderCartLoading: false,
      orderCartError: undefined,
      orderCart
    }
  },
  [LOAD_ORDER_CART_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(
      action.error.response,
      action.error.message || LOAD_ERROR
    )
    return {
      ...state,
      isOrderCartLoading: false,
      orderCart: {
        orderItems: []
      },
      orderCartError: { message }
    }
  },
  [LOAD_CHECKOUT_CART]: state => {
    return {
      ...state,
      checkoutCartError: undefined,
      isCheckoutCartLoading: true,
      editOrderError: undefined
    }
  },
  [LOAD_CHECKOUT_CART_SUCCESS]: (state, action) => {
    const { checkoutCart } = action.payload
    return {
      ...state,
      isCheckoutCartLoading: false,
      checkoutCartError: undefined,
      checkoutCart: checkoutCart.invoice.order,
      couponUsage: checkoutCart.invoice.couponUsage,
      creditUsage: checkoutCart.invoice.creditUsage,
      programUsage: checkoutCart.invoice.programUsage
    }
  },
  [LOAD_CHECKOUT_CART_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(
      action.error.response,
      action.error.message || CHECKOUT_ERROR
    )
    return {
      ...state,
      isCheckoutCartLoading: false,
      checkoutCart: {
        orderItems: []
      },
      checkoutCartError: {
        message
      }
    }
  },
  [ADD_ITEM_TO_CART]: state => {
    return {
      ...state,
      isCartSaving: true
    }
  },
  [ADD_ITEM_TO_CART_SUCCESS]: state => {
    return {
      ...state,
      isCartSaving: false,
      isEditingOrder: false,
      isEditingPastOrder: false
    }
  },
  [ADD_ITEM_TO_CART_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(
      action.error.response,
      action.error.message || ADD_ITEM_ERROR
    )
    return {
      ...state,
      isCartSaving: false,
      orderCartError: { message }
    }
  },
  [REMOVE_ITEM_FROM_ORDER_CART]: state => {
    return {
      ...state,
      isCartSaving: true
    }
  },
  [REMOVE_ITEM_FROM_ORDER_CART_SUCCESS]: state => {
    return {
      ...state,
      isCartSaving: false
    }
  },
  [REMOVE_ITEM_FROM_ORDER_CART_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(
      action.error.response,
      action.error.message || REMOVE_ITEM_ERROR
    )
    return {
      ...state,
      isCartSaving: false,
      orderCartError: { message }
    }
  },
  [REMOVE_ITEM_FROM_CHECKOUT_CART]: state => {
    return {
      ...state,
      isCartSaving: true
    }
  },
  [REMOVE_ITEM_FROM_CHECKOUT_CART_SUCCESS]: state => {
    return {
      ...state,
      isCartSaving: false
    }
  },
  [REMOVE_ITEM_FROM_CHECKOUT_CART_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(
      action.error.response,
      action.error.message || REMOVE_ITEM_ERROR
    )
    return {
      ...state,
      isCartSaving: false,
      checkoutCartError: { message }
    }
  },
  [LOAD_EDIT_ORDER]: (state, action) => {
    return {
      ...state,
      loadingOrderForEdit: true,
      isEditingOrder: false,
      isEditingPastOrder: action.payload.isEditingPastOrder
    }
  },
  [LOAD_EDIT_ORDER_SUCCESS]: (state, action) => {
    const response = action.payload
    let menuItemForEdit = response.modifiers.reduce(
      (all, { questionId: id, answers }) => {
        all[`q_${id}`] = answers.map(a => `${a.answerId}`)
        return all
      },
      {}
    )
    menuItemForEdit.menuItemId = response.menuItem
      ? response.menuItem.menuItemId
      : response.menuItemId
    menuItemForEdit.orderItemId = response.orderItemId
    menuItemForEdit.specialInstructions = response.specialInstructions
    return {
      ...state,
      loadingOrderForEdit: false,
      isEditingOrder: true,
      menuItemForEdit
    }
  },
  [LOAD_EDIT_ORDER_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(action.response)
    return {
      ...state,
      editOrderError: { message }
    }
  },
  [EDIT_ORDER_IN_CART]: state => {
    return {
      ...state,
      isEditingOrder: true
    }
  },
  [EDIT_ORDER_IN_CART_SUCCESS]: state => {
    return {
      ...state,
      isCheckoutCartLoading: false,
      isEditingOrder: false,
      loadingOrderForEdit: false,
      checkoutCartError: undefined
    }
  },
  [EDIT_ORDER_IN_CART_FAILURE]: (state, action) => {
    const message = normalizeErrorResponse(action.response)
    return {
      ...state,
      isCartSaving: false,
      isEditingOrder: false,
      checkoutCartError: { message }
    }
  },
  [CANCEL_EDITING_ORDER]: state => {
    return {
      ...state,
      isEditingOrder: false,
      isEditingPastOrder: false,
      loadingOrderForEdit: false
    }
  }
}

// ------------------------------------
// Reducer
// ------------------------------------

const initialState = {
  orderCart: {
    orderItems: []
  },
  checkoutCart: {
    orderItems: []
  },
  couponUsage: {},
  creditUsage: {
    creditBalance: 0
  },
  programUsage: {}
}

export default function cart(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

export function* watchLoadOrderCart() {
  while (true) {
    try {
      const {
        payload: { deliveryDropoffId }
      } = yield take(LOAD_ORDER_CART)

      const isAuthenticated = yield select(selectIsAuthenticated)
      if (isAuthenticated) {
        yield call(
          apiSaga,
          loadOrderCart,
          [deliveryDropoffId],
          loadOrderCartSuccess,
          loadOrderCartFailure
        )
      } else {
        yield put(loadOrderCartSuccess(getLocalCart(deliveryDropoffId)))
      }
    } catch (ex) {
      yield put(loadOrderCartFailure(ex))
    }
  }
}

export function* watchLoadCheckoutCart() {
  while (true) {
    const {
      payload: { deliveryDropoffId }
    } = yield take(LOAD_CHECKOUT_CART)
    try {
      yield call(
        apiSaga,
        loadCheckoutCart,
        [deliveryDropoffId],
        loadCheckoutCartSuccess,
        loadCheckoutCartFailure
      )
    } catch (ex) {
      logException(ex)
    }
  }
}

export const convertFormDataToMenuItem = (
  menuItemId,
  formData,
  normalizedMenuItem
) => {
  let convertedMenuItem = {
    menuItemId,
    specialInstructions: formData.specialInstructions || ''
  }
  const questionList = omit(formData, 'specialInstructions')
  convertedMenuItem.modifiers = Object.keys(questionList).map(question => {
    const questionId = Number(question.slice(question.indexOf('_') + 1))
    const answerOrAnswerArray = questionList[question]
    return {
      questionId: questionId,
      depth: normalizedMenuItem.entities.questions[questionId].depth,
      answers: Array.isArray(answerOrAnswerArray)
        ? answerOrAnswerArray.map(answer => ({ answerId: answer }))
        : [{ answerId: answerOrAnswerArray }]
    }
  })
  return convertedMenuItem
}

export const formatLocalCartMenuItem = (
  normalizedMenuItem,
  convertedMenuItem,
  menuItemId
) => {
  let total = 0.0
  const ogItem =
    normalizedMenuItem.entities.menuItems[convertedMenuItem.menuItemId]

  total += ogItem.price

  const localCartItem = {
    specialInstructions: convertedMenuItem.specialInstructions,
    menuItem: {
      itemName: ogItem.text,
      description: ogItem.description,
      displayText: ogItem.text,
      price: ogItem.price,
      menuItemId: menuItemId
    },
    menuItemId: menuItemId,
    modifiers: convertedMenuItem.modifiers.map(m => {
      return {
        questionId: m.questionId,
        depth: m.depth,
        answers: m.answers.map(a => {
          const answer = normalizedMenuItem.entities.answers[a.answerId]
          total += answer.price
          return {
            answerId: a.answerId,
            menuItem: {
              displayText: answer.text,
              description: answer.description,
              itemName: answer.text,
              price: answer.price
            }
          }
        })
      }
    })
  }

  localCartItem.fullSubTotal = total

  return localCartItem
}

export function* watchAddItemToCart() {
  while (true) {
    try {
      let {
        payload: { menuItemId, dropoffId, formData }
      } = yield take(ADD_ITEM_TO_CART)
      const normalizedMenuItem = yield select(selectNormalizedMenuItem)
      const isAuthenticated = yield select(selectIsAuthenticated)
      if (formData.orderItemId && formData.menuItemId) {
        formData = omit(formData, ['menuItemId', 'orderItemId'])
      }
      const convertedMenuItem = convertFormDataToMenuItem(
        menuItemId,
        formData,
        normalizedMenuItem
      )

      if (isAuthenticated) {
        yield call(
          apiSaga,
          addItemsToCart,
          [dropoffId, [convertedMenuItem]],
          addItemToCartSuccess,
          addItemToCartFailure
        )
      } else {
        const localCartItem = formatLocalCartMenuItem(
          normalizedMenuItem,
          convertedMenuItem,
          menuItemId
        )

        yield put(
          addItemToCartSuccess(addItemToLocalCart(dropoffId, localCartItem))
        )
      }
    } catch (err) {
      yield put(addItemToCartFailure(err))
    }
  }
}

export function* watchAddItemToAndAddItemToCartSuccess() {
  while (true) {
    const [response] = yield all([
      take(ADD_ITEM_TO_CART),
      take(ADD_ITEM_TO_CART_SUCCESS)
    ])

    const { locationId, dropoffId, checkout } = response.payload

    if (checkout) {
      const isAuthenticated = yield select(selectIsAuthenticated)

      const route = formatUrl(
        isAuthenticated ? checkoutRoute.path : checkoutInterstitialRoute.path,
        {
          locationId,
          dropoffId
        }
      )
      if (!isAuthenticated) {
        yield put(loadOrderCartStart(dropoffId))
      }
      yield put(push(route))
    } else {
      // fire an action to reload the cart
      yield put(loadOrderCartStart(dropoffId))
    }
  }
}

export function* watchEditOrder() {
  while (true) {
    try {
      const {
        payload: { hash, orderItemId, userId }
      } = yield take(LOAD_EDIT_ORDER)
      const isAuthenticated = yield select(selectIsAuthenticated)
      if (hash) {
        yield call(
          apiSaga,
          getOrderForEdit,
          [hash, userId],
          loadEditOrderSuccess,
          loadEditOrderFailure
        )
      } else if (hash === null && orderItemId && isAuthenticated) {
        yield call(
          apiSaga,
          getOrderFromCartForEdit,
          [orderItemId],
          loadEditOrderSuccess,
          loadEditOrderFailure
        )
      } else if (!isAuthenticated) {
        const { dropoffId } = yield select(selectCurrentDeliveryDropoff)
        // load from memory
        const { orderItems } = yield getLocalCart(dropoffId)
        const orderItem = orderItems.find(
          item => item.orderItemId === orderItemId
        )
        yield put(loadEditOrderSuccess(orderItem))
      }
    } catch (ex) {
      yield put(loadEditOrderFailure(ex))
    }
  }
}

export function* watchEditOrderSuccess() {
  while (true) {
    try {
      const { payload: response } = yield take(LOAD_EDIT_ORDER_SUCCESS)
      const isAuthenticated = yield select(selectIsAuthenticated)
      if (isAuthenticated) {
        yield call(
          apiSaga,
          loadMenuItem,
          [response.menuItemId],
          loadMenuItemSuccess,
          loadMenuItemFailure
        )
      } else {
        yield put(loadMenuItemSuccess(response.menuItem))
      }
    } catch (ex) {
      yield put(loadMenuItemFailure(ex))
    }
  }
}

export function* watchEditOrderInCart() {
  while (true) {
    const {
      payload: {
        locationId,
        menuItemId,
        dropoffId,
        formData,
        orderItemId,
        isEditingFromMenu,
        checkout
      }
    } = yield take(EDIT_ORDER_IN_CART)
    const normalizedMenuItem = yield select(selectNormalizedMenuItem)
    const sendingData = omit(formData, ['menuItemId', 'orderItemId'])
    const convertedMenuItem = convertFormDataToMenuItem(
      menuItemId,
      sendingData,
      normalizedMenuItem
    )
    const isAuthenticated = yield select(selectIsAuthenticated)
    const route = formatUrl(
      isAuthenticated ? checkoutRoute.path : checkoutInterstitialRoute.path,
      {
        locationId,
        dropoffId
      }
    )
    if (isAuthenticated) {
      yield call(
        apiSaga,
        editOrder,
        [orderItemId, convertedMenuItem],
        editOrderInCartSuccess,
        editOrderInCartFailure,
        checkout ? route : undefined
      )
    } else {
      const localCartItem = formatLocalCartMenuItem(
        normalizedMenuItem,
        convertedMenuItem,
        menuItemId
      )

      yield put(
        addItemToCartSuccess(
          updateItemFromLocalCart(dropoffId, localCartItem, orderItemId)
        )
      )
    }
    // fire an action to reload the cart
    if (isEditingFromMenu) {
      yield put(loadOrderCartStart(dropoffId))
    } else {
      yield put(loadCheckoutCartStart(dropoffId))
    }
  }
}

export function* watchRemoveItemFromOrderCart() {
  while (true) {
    try {
      const {
        payload: { dropoffId, menuItemId }
      } = yield take(REMOVE_ITEM_FROM_ORDER_CART)

      const isAuthenticated = yield select(selectIsAuthenticated)
      if (isAuthenticated) {
        yield call(
          apiSaga,
          removeItemFromCart,
          [dropoffId, { orderItemId: menuItemId }],
          removeItemFromOrderCartSuccess,
          removeItemFromOrderCartFailure
        )
      } else {
        yield put(
          removeItemFromOrderCartSuccess(
            removeItemFromLocalCart(dropoffId, menuItemId)
          )
        )
      }
      // fire an action to reload the cart
      yield put(loadOrderCartStart(dropoffId))
    } catch (ex) {
      yield put(removeItemFromOrderCartFailure(ex))
    }
  }
}

export function* watchRemoveItemFromCheckoutCart() {
  while (true) {
    const {
      payload: { dropoffId, menuItemId }
    } = yield take(REMOVE_ITEM_FROM_CHECKOUT_CART)

    yield call(
      apiSaga,
      removeItemFromCart,
      [dropoffId, { orderItemId: menuItemId }],
      removeItemFromCheckoutCartSuccess,
      removeItemFromCheckoutCartFailure
    )
    // fire an action to reload the cart
    yield put(loadCheckoutCartStart(dropoffId))
  }
}

export const getOrderForEdit = (hash, userId) =>
  callApi.get(
    `/api-monolith/v2/order-items/latest?hash=${hash}${
      userId ? `&userId=${userId}` : ''
    }`
  )

export const getOrderFromCartForEdit = orderItemId =>
  callApi.get(`/api-monolith/v2/orderItems/${orderItemId}`)

export const editOrder = (orderItemId, item) =>
  callApi.put(`/api-monolith/order/items/${orderItemId}`, pascalcaseKeys(item))
